/** Strings of the design screens; merged into `ca` by src/i18n/ca.ts. */
export const ca_design = {
  design: {
    panels: {
      setup: 'Configuració',
      objectives: 'Objectius',
      script: 'Guió',
      area: 'Àrea',
      markers: 'Marcadors',
      forces: 'Enemic',
      compositions: 'Composicions',
      civilians: 'Civils',
      intel: 'Intel',
      twists: 'Girs',
      briefing: 'Briefing',
      cast: 'Repartiment',
      story: 'Història',
      factions: 'Faccions',
      loadouts: 'Equipament',
      supports: 'Suports',
      rules: 'Regles',
      check: 'Comprovació',
    },
    panelRail: 'Panells del dissenyador',
    /* ---- Other editors, each in a colour of their own (edicio-a-diverses-mans). */
    editorHere: (name: string) => `${name} és en aquest panell`,
    editorChanged: (name: string) => `${name} l’ha canviat`,
    editorTyping: (name: string) => `${name} hi escriu`,
    editorOn: (name: string, where: string) => `${name}, ${where}`,
    onPanel: (panel: string) => `a ${panel}`,
    fieldTitle: 'al títol',
    fieldSummary: 'al resum',
    historyBy: (name: string) => `per ${name}`,

    /* ---- Configuració: what a generated mission lets you change afterwards. */
    setupEditHint:
      'Tot això es va decidir en generar la missió i es pot canviar aquí. Els canvis es desen com una revisió nova.',
    date: 'Data',
    dateHint:
      'Fixa l’estació, la durada del dia i la lluna. Abans totes les missions eren el 24 de juny del 2035.',
    limitMinutes: 'Durada prevista (min)',
    limitHint: 'Avisa a T-30, T-10 i a l’hora. No acaba mai la nit tota sola.',
    squadSize: 'Places per esquadra',
    squadSizeHint: 'Cada esquadra és un grup de debò amb el seu cap.',
    enemyStrength: 'Força enemiga',
    enemyStrengthNames: { light: 'Lleugera', medium: 'Mitjana', heavy: 'Pesada' },
    enemyStrengthHint:
      'L’enemic generat no es dibuixa al mapa: el servidor el posa a cada objectiu quan algú s’hi acosta. Per objectiu, Lleugera hi posa 1 escamot atrinxerat i 1 patrulla; Mitjana, 2 i 2 més un vehicle i una arma estàtica; Pesada, 3 i 3 més dos vehicles i dues armes estàtiques. Les forces que dibuixes tu al panell «Enemic» s’hi sumen.',
    civilianPresence: 'Presència civil',
    zeusForCurator: 'Zeus per a qui dirigeix',
    dryRun: 'Assaig (munta i acaba)',
    dryRunHint:
      'El servidor munta la missió, informa del que ha col·locat i acaba sense esperar ningú. És el més semblant a una previsualització d’Eden.',
    maxActiveGroups: 'Grups actius màxims',
    activationRadius: 'Radi d’activació (m)',
    headlessClients: 'Clients sense cap',
    points: 'Inserció i extracció',
    insertionPoint: 'Punt d’inserció',
    extractionPoint: 'Zona d’extracció',
    means: 'Mitjà',
    meansNames: {
      foot: 'A peu',
      vehicle: 'Vehicle',
      helicopter: 'Helicòpter',
      boat: 'Barca',
      deployed: 'Ja desplegats',
    },
    intentHint:
      'El runtime torna a comprovar aquests punts sobre el terreny quan comença la missió i els mou si el terreny els rebutja.',

    /* ---- The map's own editing surface: layers, tools and what they place. */
    layers: 'Capes',
    layerNames: {
      area: 'Àrea d’operacions',
      zones: 'Zones restringides',
      markers: 'Marcadors',
      objectives: 'Objectius',
      forces: 'Enemic i altres forces',
      compositions: 'Composicions',
      civilians: 'Civils',
      intel: 'Intel',
      places: 'Llocs del generador',
    } as Record<string, string>,
    tools: 'Eines',
    toolNames: {
      select: 'Selecciona',
      marker: 'Marcador',
      patrol: 'Patrulla',
      garrison: 'Guarnició',
      guard: 'Vigilància',
      static: 'Arma estàtica',
      vehicle: 'Vehicle',
      civilian: 'Zona civil',
      intel: 'Punt d’interès',
      area: 'Àrea d’operacions',
      zone: 'Zona restringida',
      route: 'Punt de ruta',
      composition: 'Composició',
    } as Record<string, string>,
    toolHints: {
      select: 'Clica per seleccionar; arrossega per moure.',
      marker: 'Clica al mapa per posar-hi el símbol triat.',
      patrol: 'Clica on comença la patrulla. Després hi afegeixes punts de ruta.',
      garrison: 'Clica el nucli que han de defensar des de dins dels edificis.',
      guard: 'Clica el lloc que vigilen. Després digues cap on miren.',
      static: 'Clica on va l’arma. Després digues cap on apunta.',
      vehicle: 'Clica on comença el vehicle.',
      civilian: 'Clica el centre de la zona amb població civil.',
      intel: 'Clica el punt del qual vols dir alguna cosa als jugadors.',
      area: 'Arrossega per dibuixar l’àrea on es juga la partida.',
      zone: 'Arrossega per dibuixar una zona on no s’hi pot entrar.',
      route: 'Clica per afegir un punt a la ruta de la patrulla seleccionada.',
      composition: 'Clica on va la composició. Després tria del catàleg de què és feta.',
    } as Record<string, string>,
    escToSelect: 'Prem Esc per tornar a seleccionar.',
    dragToMove: 'Arrossega qualsevol element per moure’l.',

    /* ---- Àrea */
    areaPanel: 'Àrea d’operacions',
    areaHint:
      'On es juga la partida. Els jugadors la veuen dibuixada al mapa i el runtime no genera res a fora.',
    areaNone: 'Encara no hi ha àrea. Tria l’eina «Àrea d’operacions» i arrossega sobre el mapa.',
    areaShape: 'Forma',
    shapeNames: { rect: 'Rectangle', ellipse: 'El·lipse', polygon: 'Polígon' } as Record<
      string,
      string
    >,
    areaLabel: 'Nom',
    areaWidth: 'Semi-amplada (m)',
    areaHeight: 'Semi-alçada (m)',
    areaAngle: 'Gir (°)',
    areaShowToPlayers: 'Els jugadors la veuen',
    areaRemove: 'Treu l’àrea',
    areaCentre: 'Centre',
    areaFit: 'Ajusta-la al que hi ha col·locat',

    /* ---- Zones restringides */
    zonesPanel: 'Zones restringides',
    zonesHint:
      'Fronteres, camps de mines, zones de no-foc: el que els jugadors no han de trepitjar.',
    zoneKind: 'Tipus',
    zoneKinds: {
      border: 'Frontera',
      nogo: 'Prohibit el pas',
      minefield: 'Camp de mines',
      nofire: 'No-foc',
      safe: 'Zona segura',
    } as Record<string, string>,
    zoneAdd: '+ Zona',
    zoneNone: 'Cap zona restringida.',

    /* ---- Marcadors */
    markersPanel: 'Marcadors',
    markersHint:
      'El que els jugadors veuen al mapa. Els símbols són els de l’OTAN, els mateixos que fem servir als informes.',
    markerSymbol: 'Símbol',
    markerKind: 'Mena',
    markerKinds: {
      icon: 'Símbol',
      rect: 'Rectangle',
      ellipse: 'El·lipse',
      polyline: 'Línia',
    } as Record<string, string>,
    markerText: 'Text',
    markerColour: 'Color',
    markerAudience: 'Qui el veu',
    markerAudiences: { players: 'Els jugadors', staff: 'Només els editors' } as Record<
      string,
      string
    >,
    markerReveal: 'Quan es veu',
    markerRevealStart: 'Des del principi',
    markerRevealNever: 'Quan ho digui qui dirigeix',
    markerRevealOn: (title: string) => `En resoldre «${title}»`,
    markerRevealWith: (title: string) => `En donar l’ordre de «${title}»`,
    markerAdd: '+ Marcador',
    markerNone: 'Cap marcador. Tria l’eina «Marcador» i clica al mapa.',
    markerSize: 'Mida',
    markerAlpha: 'Opacitat',
    markerPickSymbol: 'Tria un símbol',
    markerSides: 'Bàndol',
    markerRoles: 'Tipus d’unitat',
    markerPlain: 'Marques simples',

    /* ---- Forces */
    forcesPanel: 'Enemic i altres forces',
    forcesHint:
      'El que els jugadors no veuen al mapa: patrulles, guarnicions, vigilàncies, armes estàtiques i vehicles. Res d’això es dibuixa mai al mapa dels jugadors.',
    forceNone: 'Cap element col·locat. Tria una eina i clica al mapa.',
    forceRole: 'Paper',
    forceRoles: {
      patrol: 'Patrulla',
      garrison: 'Guarnició',
      guard: 'Vigilància',
      static: 'Arma estàtica',
      vehicle: 'Vehicle',
      civilian: 'Civils',
    } as Record<string, string>,
    forceRoleHints: {
      patrol: 'Recorre una ruta i la torna a començar.',
      garrison: 'Ocupa els edificis del voltant i els defensa.',
      guard: 'Es queda al lloc i mira cap on li diguis.',
      static: 'Una arma servida, amb servent, apuntada cap on li diguis.',
      vehicle: 'Un vehicle tripulat, amb ruta o aparcat.',
      civilian: 'Gent del poble que no lluita.',
    } as Record<string, string>,
    forceLabel: 'Nom',
    forceSide: 'Bàndol',
    forceSides: {
      east: 'Enemic (est)',
      west: 'Amic (oest)',
      resistance: 'Independent',
      civilian: 'Civil',
    } as Record<string, string>,
    forceFaction: 'Facció (buit = la de la missió)',
    forceCategory: 'Categoria',
    forceCategories: {
      Infantry: 'Infanteria',
      Motorized: 'Motoritzada',
      Mechanized: 'Mecanitzada',
      Armored: 'Blindada',
      Support: 'Suport',
      SpecOps: 'Forces especials',
      Sniper: 'Franctiradors',
    } as Record<string, string>,
    forceGroupClass: 'Classe de grup exacta (opcional)',
    forceGroupPreset: 'Preset de grup',
    forceGroupPresetNone: 'Cap: usa la categoria o la classe',

    /* ---- Composicions: conjunts d'objectes col·locats d'una peça. */
    compositionsHint:
      'Un conjunt d’objectes col·locat d’una peça: una base, un control de carretera, sacs terrers al voltant d’una arma. Surten del catàleg; la plataforma hi escriu els objectes quan es publica la missió.',
    compositionAdd: 'Col·loca una composició',
    compositionEmpty: 'Cap composició col·locada.',
    compositionPreset: 'Composició del catàleg',
    compositionPresetNone: 'Tria’n una…',
    compositionLabel: 'Nom',
    compositionAzimuth: 'Orientació (graus)',
    compositionAnchor: 'Va enganxada a',
    compositionAnchors: {
      none: 'Res: es queda on és',
      insertion: 'El punt d’inserció',
      extraction: 'La zona d’extracció',
    } as Record<string, string>,
    compositionSide: 'De qui és',
    compositionAudience: 'Qui la veu al mapa',
    compositionAudiences: {
      players: 'Els jugadors',
      staff: 'Només els editors',
    } as Record<string, string>,
    compositionFlatten: 'Anivella el terreny a sota',
    compositionClear: 'Radi que es neteja d’arbres i roques (m)',
    compositionObjects: (n: number) => `${n} objectes`,
    compositionNoPresets:
      'Encara no hi ha composicions al catàleg. Se n’importen des de la pàgina del catàleg.',
    forceVehicleClass: 'Classe de vehicle o arma (opcional)',
    forceRadius: 'Radi (m)',
    forceSize: 'Homes (0 = els del grup)',
    forceCount: 'Quantitat',
    forceBehaviour: 'Comportament',
    forceBehaviours: {
      CARELESS: 'Despreocupat',
      SAFE: 'Tranquil',
      AWARE: 'Alerta',
      COMBAT: 'Combat',
      STEALTH: 'Sigil',
    } as Record<string, string>,
    forceSpeed: 'Velocitat',
    forceSpeeds: { LIMITED: 'Lenta', NORMAL: 'Normal', FULL: 'Màxima' } as Record<string, string>,
    forceFormation: 'Formació',
    forceStance: 'Postura',
    forceStances: {
      AUTO: 'Automàtica',
      UP: 'Dret',
      MIDDLE: 'Agenollat',
      DOWN: 'Cos a terra',
    } as Record<string, string>,
    forceSkill: 'Perícia',
    forceWatch: 'Mira cap a',
    forceWatchSet: 'Clica al mapa per dir cap on mira',
    forceActivation: 'Quan apareix',
    forceActivations: {
      onApproach: 'Quan s’hi acosten els jugadors',
      always: 'Des del principi',
    } as Record<string, string>,
    forceActivationOn: (title: string) => `En resoldre «${title}»`,
    forceReinforces: 'Pot anar de reforç',
    forceObjective: 'Objectiu al qual pertany',
    forceCycle: 'La ruta fa el cicle',
    forceRoute: 'Ruta',
    forceRouteEmpty: 'Sense ruta: patrullarà al voltant del punt, dins el radi.',
    forceRoutePoint: (n: number) => `Punt ${n}`,
    forceRouteAdd: 'Afegeix punts a la ruta',
    forceRouteClear: 'Esborra la ruta',
    forceRemove: 'Treu aquest element',
    forceDuplicate: 'Duplica',

    /* ---- Civils */
    civiliansPanel: 'Presència civil',
    civiliansHint:
      'Gent que viu a la zona d’operacions. No lluita, però hi és, i això canvia com es juga.',
    civiliansOn: 'Hi ha civils a la zona',
    civilianZoneAdd: '+ Zona civil',
    civilianZoneNone: 'Cap zona civil.',
    civilianDensity: 'Densitat',
    civilianPanic: 'Fugen quan hi ha trets',
    civilianVehicles: 'Vehicles',
    civilianRadius: 'Radi (m)',
    civilianLabel: 'Nom del nucli',

    /* ---- Intel */
    intelPanel: 'Intel',
    intelHint:
      'El que els jugadors saben abans de sortir: punts d’interès i notes al mapa, amb quanta seguretat i d’on ve.',
    intelNone: 'Cap nota. Tria l’eina «Punt d’interès» i clica al mapa.',
    intelTitle: 'Títol',
    intelBody: 'Text',
    intelConfidence: 'Seguretat',
    intelConfidences: {
      confirmed: 'Confirmat',
      probable: 'Probable',
      unconfirmed: 'Sense confirmar',
    } as Record<string, string>,
    intelSource: 'Font',
    intelSources: ['Reconeixement', 'IMINT', 'SIGINT', 'HUMINT', 'Presoner', 'Població local'],
    intelMarker: 'Marcador relacionat',
    intelMarkerNone: 'Cap',
    intelReveal: 'Quan ho saben',
    intelRevealBriefing: 'Al briefing',
    intelRevealStart: 'En començar',
    intelRevealNever: 'Quan ho digui qui dirigeix',
    intelAdd: '+ Nota',
    intelRemove: 'Treu la nota',

    /* ---- Girs */
    twistsPanel: 'Girs',
    twistsHint:
      'El que canvia la nit a mitges: unes ordres noves per ràdio, una força de reacció en camí, el temps que es tanca, l’extracció que es mou. El text arriba al diari com una ordre fragmentària.',
    twistNone: 'Cap gir. La nit anirà com diu l’ordre.',
    twistAdd: '+ Gir',
    twistRemove: 'Treu el gir',
    twistKind: 'Què passa',
    twistKinds: {
      orders: 'Ordres noves',
      reinforcements: 'Força de reacció',
      artillery: 'Foc indirecte enemic',
      weather: 'Canvi de temps',
      extraction: 'L’extracció es mou',
      discovery: 'Una troballa',
      civilian: 'Crisi civil',
      ambush: 'Emboscada',
      informant: 'Un informador',
    } as Record<string, string>,
    twistTitle: 'Títol',
    twistBody: 'Ordre fragmentària',
    twistBodyHint: 'El que llegiran al diari, sota «Ordres fragmentàries».',
    twistRadio: 'Ràdio, una línia per línia',
    twistWhen: 'Quan passa',
    twistWhenStart: 'Al començament',
    twistWhenNever: 'Quan ho digui qui dirigeix',
    twistWhenOn: (title: string) => `En resoldre «${title}»`,
    twistWhenIntel: (title: string) => `En trobar «${title}»`,
    twistWhenMinutes: (n: number) => `Al cap de ${n} min`,
    twistMinutes: 'Minuts des de l’inici',
    twistWeather: 'Temps',
    twistReveals: 'Ensenya',
    twistCancels: 'Cancel·la',
    twistNothing: 'Res',

    /* ---- Briefing */
    briefingPanel: 'Ordre d’operació',
    briefingHowItWorks: 'Com funciona aquesta nit',
    briefingHowItWorksHint:
      'El guió de la recepta l’escriu; canvia si canvien els objectius, els suports o les ordres. Els jugadors ho llegeixen abans de l’ordre.',
    briefingHowItWorksGeneric: 'general',
    briefingHowItWorksEmpty: 'Es calcularà quan es desi la missió.',
    briefingHint:
      'Els cinc paràgrafs de sempre. El que escriguis aquí és el que llegiran al diari de la partida.',
    briefingSections: {
      context: 'Context',
      warning: 'Ordre d’avís (WARNORD)',
      situation: 'I. Situació',
      mission: 'II. Missió',
      execution: 'III. Execució',
      support: 'IV. Serveis i suports',
      command: 'V. Comandament i senyal',
      annexes: 'Annexos',
    } as Record<string, string>,
    briefingPlaceholders: {
      context: 'On som, contra qui i per què importa aquesta missió. Dues o tres frases.',
      warning: 'Què se sap i què cal preparar, abans de l’ordre sencera.',
      situation: 'Enemic, amics, terreny, població civil.',
      mission: 'Qui fa què, on, quan i per què. Una frase.',
      execution: 'Intenció, esquema de maniobra, tasques per equip, coordinació.',
      support: 'Munició, sanitat, transport, evacuació.',
      command: 'Cadena de comandament, indicatius, freqüències, marcatge.',
      annexes: 'Regles d’enfrontament, taules, indicatius, el que no cap a l’ordre.',
    } as Record<string, string>,
    briefingFromSummary: 'Comença amb el resum generat',

    /* ---- Comprovació */
    checkNone: 'Res a dir: la missió es pot publicar.',
    checkErrors: (n: number) => (n === 1 ? '1 problema' : `${n} problemes`),
    checkWarnings: (n: number) => (n === 1 ? '1 avís' : `${n} avisos`),
    checkGoTo: 'Vés-hi',
    /* ---- Locks (release D, D11.4): what the script, the recipe or the campaign decides */
    readOnlyTitle: 'Només lectura',
    lockedMove: (reason: string) => `No es pot moure: ${reason}`,
    /* ---- Additions on a scripted night (release D, D11.10) */
    forceAlertWith: (scene: string) => `Respon a l’alarma de «${scene}»`,
    forceAlertWithHint:
      'Quan salta l’alarma del guió, aquesta força es desperta amb la resta i es queda al servidor; no compta entre els homes de l’escena.',
    activeGroups: (n: number, max: number) => `Grups actius de la nit: ${n} de ${max}.`,
    activeGroupsFull:
      'El pressupost de la nit és ple: la plataforma no desarà cap força més si no en treus una o la poses en espera.',
    forceSizeRange: (min: number, max: number) =>
      `Entre ${min} i ${max}: els que l’escena necessita i els que hi caben.`,
    forceSizeFixed: (n: number) => `Mida: ${n} (la decideix el guió).`,
    objectiveRole: 'Paper',
    objectiveRoles: {
      secondary: 'Secundari: no canvia com acaba la nit',
      additional: 'Addicional: si queda per fer, la nit acaba parcialment complerta',
    } as Record<string, string>,
    twistFixedWhen: (words: string) => `Quan: ${words}. Ho decideix el guió.`,
    twistAtWords: (at: string | undefined, phase: (id: string) => string, until?: string) => {
      const from = !at
        ? 'a l’inici'
        : at.startsWith('onPhase:')
          ? `quan comença la fase «${phase(at.slice(8))}»`
          : at.startsWith('T+')
            ? `${at.slice(2).replace('m', '')} minuts després de l’inici`
            : at;
      return until ? `${from}, fins a la fase «${phase(until)}»` : from;
    },
    fixedTitle: 'El que decideix el tipus',
    fixedHour: 'Hora',
    fixedWeather: 'Temps',
    fixedDate: 'Data',
    fixedByRegeneration: 'Es canvia tornant a generar la missió.',
    /* ---- Paràmetres del tipus (release D, D11.14): changed only by generating the mission again */
    recipeParams: {
      title: 'Paràmetres del tipus',
      hint: 'En una missió amb guió aquests valors només es canvien tornant-la a generar: el mateix codi, la mateixa llavor i el mateix títol, i el guió ho recalcula tot. Només s’hi ofereix el que el tipus admet.',
      hour: 'Hora d’inici',
      date: 'Data',
      players: 'Jugadors',
      squadSize: 'Places per esquadra',
      playersRange: (min: number, max: number) =>
        `El tipus està pensat per a ${min}–${max} jugadors.`,
      playersOutside: (min: number, max: number) =>
        `El tipus està pensat per a ${min}–${max} jugadors: es generarà igualment, amb un avís.`,
      insertion: 'Entrada',
      extraction: 'Sortida',
      meansNames: {
        foot: 'A peu',
        vehicle: 'En vehicle',
        helicopter: 'En helicòpter',
        boat: 'En barca',
        parachute: 'En paracaigudes',
        deployed: 'Ja desplegats',
      } as Record<string, string>,
      startFrom: 'D’on surt la nit',
      startNames: { land: 'De terra', sea: 'Del mar' } as Record<string, string>,
      supports: 'Suports',
      enemyStrength: 'Força enemiga',
      regenerate: 'Torna a generar amb aquests paràmetres',
      undo: 'Desfés',
    },
    /** A problem the platform found in the saved revision (the schema, the script's wiring). */
    checkStored: (reason: string) => `A la versió desada: ${reason}`,
    /* ---- Pre-play check (release D, D11.9): what the platform would move before play */
    preflightKinds: {
      objective: 'L’objectiu',
      force: 'La força',
      waypoint: 'Un punt de ruta de la força',
      composition: 'La composició',
      intel: 'La nota',
      civilian: 'La zona civil',
    } as Record<string, string>,
    preflightChecks: {
      water: 'és a l’aigua',
      edge: 'és a la vora del mapa',
      landMass: 'és en una altra illa que la base',
      base: 'és a menys de 500 m de la base',
    } as Record<string, string>,
    preflightMoved: (what: string, label: string, check: string, metres: number) =>
      `Abans de jugar es mourà: ${what} «${label}» ${check}; anirà ${metres} m més enllà.`,
    preflightUnplaceable: (what: string, label: string, check: string) =>
      `${what} «${label}» ${check} i no hi ha cap punt vàlid a prop: cal moure-ho a mà abans de jugar.`,
    preflightLeft: (what: string, label: string, check: string) => `${what} «${label}» ${check}.`,
    /* The mission page's report (D11.9, second half): what the platform corrected before play. */
    preflightCard: 'Correccions abans de jugar',
    preflightCardHint:
      'Abans de cada partida, la plataforma comprova amb la inspecció del mapa les posicions que es poden editar i mou el que el joc no podria plantar. El que només el joc pot mesurar, ho decideix el joc.',
    preflightDone: (revision: number) => `A la versió r${revision}, la plataforma va moure:`,
    preflightDoneRow: (what: string, label: string, check: string, metres: number) =>
      `${what} «${label}» ${check}: s’ha mogut ${metres} m.`,
    preflightNext: 'A la propera partida es mourà:',
    preflightStuck: 'No es podrà jugar fins que es mogui a mà:',
    preflightSaid: 'Només s’avisa, no es mou:',
    preflightNothing: 'Res per corregir.',
    preflightUnsurveyed: 'El mapa no té cap inspecció completa: el joc decidirà cada posició.',
    platformAuthor: 'la plataforma',

    /* ---- Shared */
    remove: 'Treu',
    add: 'Afegeix',
    none: 'Cap',
    position: 'Posició',
    metres: (n: number) => `${Math.round(n)} m`,

    generateWhole: 'Genera: tota la missió',
    generatePanel: 'Genera: només aquest panell',
    preview: 'Previsualitza',
    publish: 'Publica',
    historyButton: 'Historial',
    closeHistory: 'Tanca l’historial',
    restore: 'Restaura',

    downloadTitle: 'Emporta-te-la',
    deleteHint:
      'Esborrar la missió la treu de la biblioteca i del servidor. Les partides jugades i els seus informes es mantenen, i la pots recuperar des de la paperera de la biblioteca.',
    setupHint: 'Generada amb la missió; encara no es pot editar des d’aquí.',
    terrain: 'Terreny',
    playerSlots: 'Places de jugador',
    duration: 'Durada',
    timeOfDay: 'Hora del dia',
    weather: 'Temps',
    persistence: 'Persistència',
    difficulty: 'Dificultat',
    notGenerated: '—',

    castHint:
      'Qui hi surt, amb el rol i la disposició de cadascú. Canvia’n el nom i es canvia també allà on la missió el diu; «Torna a tirar» en treu un de nou, i un personatge bloquejat no es toca.',
    reroll: 'Torna a tirar',
    rerollAll: 'Torna’ls a tirar tots',
    rerollFailed: 'No s’ha pogut tornar a tirar. Prova-ho de nou.',
    lock: 'Bloqueja',
    unlock: 'Desbloqueja',
    addCastMember: '+ Personatge',
    removeCastMember: 'Treu-lo',
    newCastName: 'Personatge nou',
    castName: 'Nom',
    dispositions: {
      hostile: 'Hostil',
      wary: 'Recelós',
      neutral: 'Neutral',
      friendly: 'Amistós',
    } as Record<string, string>,
    castRole: 'Rol',
    castDisposition: 'Disposició',
    noCast: 'Encara no hi ha repartiment generat.',

    storyHint:
      'El resum de la missió, tal com es llegeix a la biblioteca. Si la missió es genera de nou, el que hagis escrit es conserva allà on encara té lloc.',
    storySummary: 'Resum',
    regenerateParagraph: 'Torna a generar aquest paràgraf',
    storyByHand:
      'El resum està escrit a mà: no hi ha res del generador que es pugui tornar a tirar. Esborra’l per començar de nou.',
    noStory: 'Encara no hi ha resum.',

    factionsHint: 'Qui són els jugadors i qui l’enemic.',
    factionsApply: 'Canvia les faccions i genera de nou',
    factionsRegenerates:
      'Canviar una facció genera la missió de nou amb el mateix codi i llavor: les forces, els vehicles i els noms hi segueixen. El que has escrit a mà es conserva allà on encara té lloc.',
    factionsUnavailable: 'No s’ha pogut llegir el catàleg de faccions d’aquest conjunt de mods.',
    playersFaction: 'Facció dels jugadors',
    enemyFaction: 'Facció enemiga',
    loadoutsHint:
      'L’equipament de cada rol es valida, s’importa i s’edita a la biblioteca, a la pestanya d’equipaments; la missió fa servir el que hi has desat.',
    loadoutsLink: 'Obre els equipaments a la biblioteca',

    rulesHint: 'La matriu de decisions i les finestres de vot, com al pas 3 de l’inici ràpid.',
    respawnRules: 'Regles de reaparició',
    respawnModel: {
      hint: 'Si qui cau pot tornar a la partida, i com. Cada servidor té una manera per defecte, que de sèrie és que ningú no reapareix: qui cau mira la partida com a espectador.',
      unseen:
        'Només la partida sense reaparició s’ha jugat fins ara; aquesta manera encara no s’ha vist en una partida.',
      mode: 'Reaparició',
      modes: {
        server: 'Per defecte del servidor',
        none: 'Cap: qui cau mira',
        base: 'A la base',
        leader: 'Al costat del cap d’esquadra',
        lives: 'Amb vides',
        waves: 'Per onades',
      } as Record<string, string>,
      modeHints: {
        server:
          'Fa el que digui el servidor on es juga la nit. Si no s’ha canviat, ningú no reapareix.',
        none: 'Qui cau mira la partida com a espectador fins que acaba.',
        base: 'Qui cau torna a la base de sortida passat el temps d’espera.',
        leader:
          'Qui cau torna a la base i hi surt al costat del cap d’esquadra; si el cap ha caigut, del primer company que viu. No cal plantar cap punt de reunió.',
        lives:
          'Cada jugador té unes quantes vides, o bé es reparteixen entre tots. Qui les gasta mira la partida.',
        waves: 'Qui cau espera i tots els caiguts tornen alhora, cada tants minuts.',
      } as Record<string, string>,
      delay: 'Espera abans de tornar (segons)',
      lives: 'Vides',
      shared: 'Vides compartides per tot el bàndol',
      waveMinutes: 'Minuts entre onades',
    },
    viewDistance: 'Distància de visió recomanada (m)',
    viewDistanceDefault: 'La del servidor',
    viewDistanceHint:
      'Es mostra com a recomanació; el joc encara no l’aplica sola. Buit: la del servidor.',

    checkHint: 'La llista de comprovació abans de publicar.',
    checkItems: [
      'Node inabastable',
      'Cost per sobre del pressupost del servidor',
      'Classe d’equipament que falta al catàleg',
      'Node ocult sense activador de revelació',
      'Briefing per sobre de la mida',
    ],
  },
};
